import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Mail, Send, Loader2, ChevronDown, ChevronUp, Eye, Server,
  Paperclip, X, FileText, Image as ImageIcon, File as FileIcon,
  Minimize2, Maximize2, Minus, Upload, AlertCircle,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@easyflow/ui-frontend/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import RichTextEditor from '@easyflow/ui-frontend/components/ui/rich-text-editor';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { cn, getErrorMessage } from '@/lib/utils';

interface UnifiedEmailAccount {
  id: string;
  account_type: 'gmail' | 'smtp';
  display_name: string;
  email_address: string;
  is_default: boolean;
  signature_html?: string | null;
  signature_enabled?: boolean;
  signature_on_reply?: boolean;
}

const SIGNATURE_MARKER_REGEX_ANY = /<div[^>]*data-signature-marker="1"[^>]*>[\s\S]*?<\/div>/gi;
const QUOTED_REPLY_REGEX = /(<(?:div|blockquote)[^>]*(?:class="gmail_quote"|data-quoted-reply="1")[\s\S]*$)/i;

function buildSignatureBlock(accountId: string, html: string): string {
  return `<div data-signature="${accountId}" data-signature-marker="1"><br/>--<br/>${html}</div>`;
}

function injectOrReplaceSignature(body: string, accountId: string | null, signatureHtml: string | null | undefined): string {
  const stripped = (body || '').replace(SIGNATURE_MARKER_REGEX_ANY, '');
  if (!accountId || !signatureHtml || !signatureHtml.trim()) return stripped;
  const block = buildSignatureBlock(accountId, signatureHtml);
  const m = stripped.match(QUOTED_REPLY_REGEX);
  if (m && m.index !== undefined) {
    return stripped.slice(0, m.index) + block + stripped.slice(m.index);
  }
  return stripped + block;
}

interface EmailComposeModalProps {
  open: boolean;
  onClose: () => void;
  mode: 'compose' | 'reply';
  replyData?: {
    threadId: string;
    to: string;
    subject: string;
    originalBody?: string;
  };
  defaultTo?: string;
  defaultName?: string;
  leadId?: string;
  customerId?: string;
  campaignId?: string;
  accountId?: string;
  onSent?: () => void;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  is_default?: boolean;
}

interface AttachedFile {
  file: File;
  id: string;
}

const MAX_TOTAL_SIZE = 25 * 1024 * 1024;
const MAX_FILES = 10;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return ImageIcon;
  if (type.includes('pdf') || type.includes('document') || type.includes('text')) return FileText;
  return FileIcon;
}

export default function EmailComposeModal({
  open,
  onClose,
  mode,
  replyData,
  defaultTo,
  defaultName,
  leadId,
  customerId,
  campaignId,
  accountId,
  onSent,
}: EmailComposeModalProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const [tab, setTab] = useState<string>('free');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(false);

  const [emailAccounts, setEmailAccounts] = useState<UnifiedEmailAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [windowSize, setWindowSize] = useState<'full' | 'compact'>('full');
  const [minimized, setMinimized] = useState(false);
  const [mobilePane, setMobilePane] = useState<'email' | 'attachments' | 'extra'>('email');
  const [isDragging, setIsDragging] = useState(false);

  const initialTo = useMemo(() => {
    if (mode === 'reply' && replyData) return replyData.to;
    return defaultTo || '';
  }, [mode, replyData, defaultTo]);

  const initialSubject = useMemo(() => {
    if (mode === 'reply' && replyData) {
      return replyData.subject.startsWith('Re:') ? replyData.subject : `Re: ${replyData.subject}`;
    }
    return '';
  }, [mode, replyData]);

  const loadEmailAccounts = useCallback(async () => {
    try {
      const res = await apiClient.get<{ data?: UnifiedEmailAccount[] }>('/email-accounts/all');
      if (res.success) {
        const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        setEmailAccounts(list);
        if (accountId) {
          setSelectedAccountId(accountId);
        } else {
          const def = list.find((a: UnifiedEmailAccount) => a.is_default);
          setSelectedAccountId(def?.id || (list.length > 0 ? list[0].id : ''));
        }
      }
    } catch (err) {
      console.error('Error loading email accounts:', err);
    }
  }, [accountId]);

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await apiClient.get<{ templates: EmailTemplate[] }>('/email/templates');
      if (res.success && res.data) {
        const list = Array.isArray(res.data.templates) ? res.data.templates : Array.isArray(res.data) ? res.data : [];
        setTemplates(list);
      }
    } catch (error) {
      console.error('Error loading email templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    if (open) {
      setTo(initialTo);
      setSubject(initialSubject);
      setHtmlBody('');
      setCc('');
      setBcc('');
      setShowCcBcc(false);
      setTab('free');
      setSelectedTemplateId('');
      setTemplateSubject('');
      setTemplateBody('');
      setEditingTemplate(false);
      setSending(false);
      setAttachments([]);
      setConfirmCloseOpen(false);
      setMinimized(false);
      setWindowSize('full');
      setMobilePane('email');
      setIsDragging(false);
      dragCounter.current = 0;
      loadTemplates();
      loadEmailAccounts();
    }
  }, [open, initialTo, initialSubject, loadEmailAccounts]);

  useEffect(() => {
    if (!open) return;
    if (!selectedAccountId || emailAccounts.length === 0) return;

    const acct = emailAccounts.find(a => a.id === selectedAccountId);
    if (!acct) return;

    const enabled = acct.signature_enabled !== false;
    const onReply = acct.signature_on_reply !== false;
    const allowed = enabled && (mode !== 'reply' || onReply);
    const sigHtml = allowed ? (acct.signature_html || '') : '';

    setHtmlBody(prev => injectOrReplaceSignature(prev, selectedAccountId, sigHtml));
    setTemplateBody(prev => prev ? injectOrReplaceSignature(prev, selectedAccountId, sigHtml) : prev);
  }, [selectedAccountId, emailAccounts, open, mode]);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const tpl = templates.find(t => t.id === templateId);
    if (tpl) {
      setTemplateSubject(tpl.subject || '');
      const acct = emailAccounts.find(a => a.id === selectedAccountId);
      const enabled = acct ? acct.signature_enabled !== false : true;
      const onReply = acct ? acct.signature_on_reply !== false : true;
      const allowed = !!acct && enabled && (mode !== 'reply' || onReply);
      const sigHtml = allowed ? (acct?.signature_html || '') : '';
      setTemplateBody(injectOrReplaceSignature(tpl.body || '', selectedAccountId || null, sigHtml));
      setEditingTemplate(false);
    }
  };

  const addFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    const newFiles: AttachedFile[] = [];
    let totalSize = attachments.reduce((sum, a) => sum + a.file.size, 0);

    for (let i = 0; i < arr.length; i++) {
      const file = arr[i];
      if (file!.size! > MAX_TOTAL_SIZE) {
        toast({ title: 'File troppo grande', description: `${file!.name } supera il limite di 25MB`, variant: 'destructive' });
        continue;
      }
      totalSize += file!.size!;
      if (totalSize > MAX_TOTAL_SIZE) {
        toast({ title: 'Limite raggiunto', description: 'La dimensione totale degli allegati supera 25MB', variant: 'destructive' });
        break;
      }
      if (attachments.length + newFiles.length >= MAX_FILES) {
        toast({ title: 'Limite raggiunto', description: `Massimo ${MAX_FILES} allegati per email`, variant: 'destructive' });
        break;
      }
      newFiles.push({ file: file!, id: `${Date.now()}_${i}_${file!.name }` });
    }

    if (newFiles.length > 0) {
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    addFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    if (sending) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (sending) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (sending) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    if (sending) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleSend = async () => {
    const finalTo = to.trim();
    if (!finalTo) {
      toast({ title: 'Errore', description: 'Inserisci un destinatario', variant: 'destructive' });
      return;
    }

    const isTemplate = tab === 'template' && selectedTemplateId;
    const finalSubject = isTemplate ? templateSubject : subject;
    const finalHtmlBody = isTemplate ? templateBody : htmlBody;

    if (!finalSubject.trim()) {
      toast({ title: 'Errore', description: 'Inserisci un oggetto', variant: 'destructive' });
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('to', finalTo);
      formData.append('subject', finalSubject);
      formData.append('html_body', finalHtmlBody);
      formData.append('body', finalHtmlBody.replace(/<[^>]*>/g, ''));

      if (cc.trim()) formData.append('cc', cc.trim());
      if (bcc.trim()) formData.append('bcc', bcc.trim());
      if (leadId) formData.append('lead_id', leadId);
      if (customerId) formData.append('customer_id', customerId);
      if (campaignId) formData.append('campaign_id', campaignId);
      if (selectedAccountId) formData.append('account_id', selectedAccountId);
      else if (accountId) formData.append('account_id', accountId);
      if (isTemplate) formData.append('template_id', selectedTemplateId);

      if (mode === 'reply' && replyData) {
        formData.append('thread_id', replyData.threadId);
      }

      for (const att of attachments) {
        formData.append('attachments', att.file, att.file.name);
      }

      const endpoint = mode === 'reply' && replyData ? '/email/reply' : '/email/send';

      const res = await apiClient.post<{ success: boolean; error?: string }>(endpoint, formData);
      if (!res.success) {
        throw new Error(res.error || 'Errore invio email');
      }

      toast({ title: 'Email inviata', description: `Email inviata a ${finalTo}` });
      onSent?.();
      onClose();
    } catch (err) {
      toast({
        title: 'Errore',
        description: getErrorMessage(err) || 'Errore durante l\'invio dell\'email',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const totalAttachmentSize = attachments.reduce((sum, a) => sum + a.file.size, 0);

  const isDirty = useMemo(() => {
    return (
      htmlBody.trim() !== '' ||
      templateBody.trim() !== '' ||
      to.trim() !== initialTo.trim() ||
      subject.trim() !== initialSubject.trim() ||
      attachments.length > 0 ||
      cc.trim() !== '' ||
      bcc.trim() !== ''
    );
  }, [htmlBody, templateBody, to, subject, attachments, cc, bcc, initialTo, initialSubject]);

  const requestClose = () => {
    if (sending) return;
    if (isDirty) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const performClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  const headerTitle = mode === 'reply' ? 'Rispondi' : 'Componi Email';
  const headerSubtitle = mode === 'reply'
    ? `Risposta a ${replyData?.to || ''}`
    : defaultName
      ? `Nuova email a ${defaultName}`
      : (to ? `Nuova email a ${to}` : 'Nuova email');

  if (!open) return null;

  return (
    <>
      {minimized ? (
        <div className="fixed bottom-4 right-4 z-[60]">
          <button
            type="button"
            onClick={() => setMinimized(false)}
            className="flex items-center gap-3 pl-4 pr-2 py-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-2xl hover:shadow-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all max-w-[360px]"
            data-testid="button-restore-compose"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-950 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-left min-w-0">
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {headerTitle}
                </p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                  {(tab === 'template' ? templateSubject : subject) || headerSubtitle}
                </p>
              </div>
            </div>
            {isDirty && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" title="Bozza non salvata" />
            )}
            <span
              role="button"
              tabIndex={0}
              aria-label="Chiudi"
              onClick={(e) => { e.stopPropagation(); requestClose(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); requestClose(); } }}
              className="ml-1 p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition-colors cursor-pointer"
              data-testid="button-close-from-pill"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          </button>
        </div>
      ) : (
        <Dialog open={open} onOpenChange={() => { /* close handled only via X */ }}>
          <DialogContent
            hideClose
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className={cn(
              "overflow-hidden shadow-2xl",
              "sm:!p-0 sm:!gap-0 max-sm:!p-0 max-sm:!gap-0",
              "sm:!max-w-none sm:!rounded-xl",
              windowSize === 'full'
                ? "sm:!w-[95vw] sm:!max-h-[95vh] sm:!h-[95vh]"
                : "sm:!w-[80vw] sm:!max-w-[1100px] sm:!max-h-[85vh] sm:!h-[85vh]",
              "sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2"
            )}
            style={windowSize === 'full' ? { maxWidth: '1400px' } : undefined}
            onDragEnterCapture={handleDragEnter}
            onDragLeaveCapture={handleDragLeave}
            onDragOverCapture={handleDragOver}
            onDropCapture={handleDrop}
            data-testid="dialog-compose-email"
          >
            <DialogTitle className="sr-only">{headerTitle}</DialogTitle>
            <DialogDescription className="sr-only">{headerSubtitle}</DialogDescription>

            <div className="flex flex-col h-full max-h-full bg-gray-50 dark:bg-gray-950">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight truncate">
                      {headerTitle}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {headerSubtitle}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setMinimized(true)}
                    disabled={sending}
                    title="Riduci"
                    aria-label="Riduci"
                    className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                    data-testid="button-minimize-compose"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setWindowSize(s => s === 'full' ? 'compact' : 'full')}
                    disabled={sending}
                    title={windowSize === 'full' ? 'Riduci finestra' : 'Schermo intero'}
                    aria-label={windowSize === 'full' ? 'Riduci finestra' : 'Schermo intero'}
                    className="hidden sm:inline-flex p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                    data-testid="button-toggle-fullscreen-compose"
                  >
                    {windowSize === 'full' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </button>
                  <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />
                  <button
                    type="button"
                    onClick={requestClose}
                    disabled={sending}
                    title="Chiudi"
                    aria-label="Chiudi"
                    className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-950 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                    data-testid="button-close-compose"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Mobile pane switcher */}
              <div className="lg:hidden flex items-center gap-1 px-3 py-2 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setMobilePane('email')}
                  className={cn(
                    "flex-1 py-1.5 text-xs font-medium rounded-md transition-colors",
                    mobilePane === 'email'
                      ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                      : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  )}
                  data-testid="tab-mobile-email"
                >
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setMobilePane('attachments')}
                  className={cn(
                    "flex-1 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center justify-center gap-1.5",
                    mobilePane === 'attachments'
                      ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                      : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  )}
                  data-testid="tab-mobile-attachments"
                >
                  Allegati
                  {attachments.length > 0 && (
                    <span className="text-[10px] font-semibold bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                      {attachments.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setMobilePane('extra')}
                  className={cn(
                    "flex-1 py-1.5 text-xs font-medium rounded-md transition-colors",
                    mobilePane === 'extra'
                      ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300"
                      : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  )}
                  data-testid="tab-mobile-extra"
                >
                  {mode === 'reply' ? 'Template / Originale' : 'Template / Info'}
                </button>
              </div>

              {/* Body: 2-column on lg, single pane on mobile */}
              <div className="flex-1 min-h-0 overflow-hidden grid lg:grid-cols-[1fr_minmax(320px,420px)]">
                {/* LEFT: Email composition */}
                <div className={cn(
                  "flex flex-col min-h-0 overflow-y-auto bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800",
                  "lg:flex",
                  mobilePane === 'email' ? 'flex' : 'hidden'
                )}>
                  <div className="p-5 space-y-4 flex-1 flex flex-col min-h-0">
                    {/* Account selector */}
                    <div>
                      <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">Da</Label>
                      {emailAccounts.length > 0 ? (
                        <Select value={selectedAccountId} onValueChange={setSelectedAccountId} disabled={sending}>
                          <SelectTrigger className="mt-1 h-9" data-testid="select-from-account">
                            <SelectValue placeholder="Seleziona account mittente..." />
                          </SelectTrigger>
                          <SelectContent>
                            {emailAccounts.map((acct) => (
                              <SelectItem key={acct.id} value={acct.id}>
                                <span className="flex items-center gap-2">
                                  {acct.account_type === 'gmail' ? (
                                    <Mail className="w-3 h-3 text-red-500 inline-block" />
                                  ) : (
                                    <Server className="w-3 h-3 text-blue-500 inline-block" />
                                  )}
                                  {acct.display_name} ({acct.email_address})
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="mt-1 px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md text-sm text-gray-500 dark:text-gray-400">
                          Account predefinito
                        </div>
                      )}
                    </div>

                    {/* Recipients */}
                    <div>
                      <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">A *</Label>
                      <Input
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="email@esempio.com"
                        type="email"
                        disabled={sending}
                        className="mt-1 h-9"
                        data-testid="input-recipient-to"
                      />
                    </div>

                    <div>
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                        onClick={() => setShowCcBcc(!showCcBcc)}
                        disabled={sending}
                        data-testid="button-toggle-cc-bcc"
                      >
                        {showCcBcc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        CC / BCC
                      </button>
                      {showCcBcc && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                          <div>
                            <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">CC</Label>
                            <Input
                              value={cc}
                              onChange={(e) => setCc(e.target.value)}
                              placeholder="cc@esempio.com"
                              disabled={sending}
                              className="mt-1 h-9"
                              data-testid="input-cc"
                            />
                          </div>
                          <div>
                            <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">BCC</Label>
                            <Input
                              value={bcc}
                              onChange={(e) => setBcc(e.target.value)}
                              placeholder="bcc@esempio.com"
                              disabled={sending}
                              className="mt-1 h-9"
                              data-testid="input-bcc"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Tabs Free/Template */}
                    <Tabs value={tab} onValueChange={setTab} className="w-full flex-1 flex flex-col min-h-0">
                      <TabsList className="w-full">
                        <TabsTrigger value="free" className="flex-1" disabled={sending} data-testid="tab-free">
                          Testo Libero
                        </TabsTrigger>
                        <TabsTrigger value="template" className="flex-1" disabled={sending} data-testid="tab-template">
                          Da Template
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="free" className="space-y-3 mt-3 flex-1 flex flex-col min-h-0 data-[state=inactive]:hidden">
                        <div>
                          <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">Oggetto *</Label>
                          <Input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Oggetto dell'email"
                            disabled={sending}
                            className="mt-1 h-9"
                            data-testid="input-subject"
                          />
                        </div>
                        <div className="flex-1 flex flex-col min-h-0">
                          <Label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">Corpo</Label>
                          <div className="flex-1 min-h-[400px]">
                            <RichTextEditor
                              value={htmlBody}
                              onChange={setHtmlBody}
                              placeholder="Scrivi il contenuto dell'email..."
                              minHeight={400}
                              className="h-full"
                            />
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="template" className="space-y-3 mt-3 flex-1 flex flex-col min-h-0 data-[state=inactive]:hidden">
                        {loadingTemplates ? (
                          <div className="flex items-center gap-2 text-sm text-gray-500 py-4 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento template...
                          </div>
                        ) : templates.length === 0 ? (
                          <div className="text-sm text-gray-500 text-center py-4">
                            Nessun template disponibile
                          </div>
                        ) : (
                          <>
                            <div>
                              <Label className="text-xs font-medium text-gray-500 dark:text-gray-400">Seleziona Template</Label>
                              <Select value={selectedTemplateId} onValueChange={handleTemplateSelect} disabled={sending}>
                                <SelectTrigger className="mt-1" data-testid="select-template">
                                  <SelectValue placeholder="Scegli un template..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {templates.map((tpl) => (
                                    <SelectItem key={tpl.id} value={tpl.id}>
                                      {tpl.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {selectedTemplateId && (
                              <div className="flex-1 flex flex-col min-h-0 space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs text-gray-500 dark:text-gray-400">
                                    {editingTemplate ? 'Modifica template' : 'Anteprima template'}
                                  </Label>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs gap-1"
                                    onClick={() => setEditingTemplate(!editingTemplate)}
                                    disabled={sending}
                                    data-testid="button-toggle-template-edit"
                                  >
                                    <Eye className="w-3 h-3" />
                                    {editingTemplate ? 'Anteprima' : 'Modifica'}
                                  </Button>
                                </div>

                                {editingTemplate ? (
                                  <div className="space-y-2 flex-1 flex flex-col min-h-0">
                                    <div>
                                      <Label className="text-xs">Oggetto</Label>
                                      <Input
                                        value={templateSubject}
                                        onChange={(e) => setTemplateSubject(e.target.value)}
                                        disabled={sending}
                                        className="mt-1"
                                        data-testid="input-template-subject"
                                      />
                                    </div>
                                    <div className="flex-1 flex flex-col min-h-0">
                                      <Label className="text-xs">Corpo</Label>
                                      <div className="flex-1 min-h-[300px] mt-1">
                                        <RichTextEditor
                                          value={templateBody}
                                          onChange={setTemplateBody}
                                          placeholder="Corpo del template..."
                                          minHeight={300}
                                          className="h-full"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm flex-1 overflow-y-auto">
                                    <p className="font-medium text-gray-700 dark:text-gray-200 mb-2">
                                      Oggetto: {templateSubject}
                                    </p>
                                    <div
                                      className="text-gray-600 dark:text-gray-300 prose prose-sm max-w-none dark:prose-invert"
                                      dangerouslySetInnerHTML={{ __html: templateBody }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>

                {/* RIGHT: side panel */}
                <aside className={cn(
                  "flex flex-col min-h-0 overflow-y-auto bg-gray-50 dark:bg-gray-950",
                  "lg:flex",
                  (mobilePane === 'attachments' || mobilePane === 'extra') ? 'flex' : 'hidden'
                )}>
                  <div className="p-4 space-y-4">
                    {/* Attachments */}
                    <section className={cn(
                      "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden",
                      "lg:block",
                      mobilePane === 'attachments' ? 'block' : 'hidden lg:block'
                    )}>
                      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200 dark:border-gray-800">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Allegati</span>
                          <span className="text-xs text-gray-400">
                            {attachments.length}/{MAX_FILES}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={sending || attachments.length >= MAX_FILES}
                          data-testid="button-add-attachment"
                        >
                          <Paperclip className="w-3 h-3" />
                          Aggiungi
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileSelect}
                        />
                      </div>

                      <div
                        className="p-3 space-y-2"
                        data-testid="dropzone-attachments"
                      >
                        {/* Dropzone hint */}
                        <div className="flex flex-col items-center justify-center gap-1.5 py-4 px-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg bg-gray-50/60 dark:bg-gray-800/40 text-center">
                          <Upload className="w-5 h-5 text-gray-400" />
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Trascina i file qui per allegarli
                          </p>
                          <p className="text-[10px] text-gray-400">
                            Max {MAX_FILES} file · {formatFileSize(MAX_TOTAL_SIZE)} totali
                          </p>
                        </div>

                        {attachments.length > 0 && (
                          <div className="space-y-1.5 pt-1">
                            {attachments.map((att) => {
                              const Icon = getFileIcon(att.file.type);
                              return (
                                <div
                                  key={att.id}
                                  className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md group"
                                  data-testid={`attachment-${att.id}`}
                                >
                                  <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <span className="text-xs text-gray-700 dark:text-gray-200 truncate flex-1">
                                    {att.file.name}
                                  </span>
                                  <span className="text-xs text-gray-400 flex-shrink-0">
                                    {formatFileSize(att.file.size)}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(att.id)}
                                    disabled={sending}
                                    className="p-0.5 text-gray-400 hover:text-red-500 transition-opacity disabled:opacity-30"
                                    aria-label={`Rimuovi ${att.file.name}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {totalAttachmentSize > 0 && (
                          <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 pt-1">
                            <span>Totale</span>
                            <span className={cn(
                              "font-medium",
                              totalAttachmentSize > MAX_TOTAL_SIZE * 0.9 && "text-amber-600 dark:text-amber-400"
                            )}>
                              {formatFileSize(totalAttachmentSize)} / {formatFileSize(MAX_TOTAL_SIZE)}
                            </span>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Template summary (when template tab is active) */}
                    {tab === 'template' && selectedTemplateId && (
                      <section className={cn(
                        "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden",
                        "lg:block",
                        mobilePane === 'extra' ? 'block' : 'hidden lg:block'
                      )}>
                        <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Template selezionato
                          </span>
                        </div>
                        <div className="p-3 space-y-2 max-h-[260px] overflow-y-auto">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="font-medium text-gray-700 dark:text-gray-200">
                              {templates.find(t => t.id === selectedTemplateId)?.name || 'Template'}
                            </span>
                          </p>
                          <div className="pt-1 border-t border-gray-100 dark:border-gray-800">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Oggetto</p>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200 break-words">
                              {templateSubject || <span className="italic text-gray-400">— vuoto —</span>}
                            </p>
                          </div>
                          <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Anteprima</p>
                            <div
                              className="text-xs text-gray-600 dark:text-gray-300 prose prose-xs max-w-none dark:prose-invert line-clamp-6"
                              dangerouslySetInnerHTML={{ __html: templateBody || '<em class="text-gray-400">— vuoto —</em>' }}
                            />
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Reply original */}
                    {mode === 'reply' && replyData?.originalBody && (
                      <section className={cn(
                        "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden",
                        "lg:block",
                        mobilePane === 'extra' ? 'block' : 'hidden lg:block'
                      )}>
                        <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Messaggio originale
                          </span>
                        </div>
                        <div className="p-3 max-h-[280px] overflow-y-auto">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Da: {replyData.to}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                            {replyData.originalBody}
                          </p>
                        </div>
                      </section>
                    )}

                    {/* Context metadata */}
                    {(leadId || customerId || campaignId || defaultName) && (
                      <section className={cn(
                        "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden",
                        "lg:block",
                        mobilePane === 'extra' ? 'block' : 'hidden lg:block'
                      )}>
                        <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Contesto
                          </span>
                        </div>
                        <div className="p-3 space-y-1.5 text-xs">
                          {defaultName && (
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-500 dark:text-gray-400">Destinatario</span>
                              <span className="font-medium text-gray-700 dark:text-gray-200 truncate">{defaultName}</span>
                            </div>
                          )}
                          {leadId && (
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-500 dark:text-gray-400">Lead ID</span>
                              <span className="font-mono text-[10px] text-gray-700 dark:text-gray-200 truncate">{leadId}</span>
                            </div>
                          )}
                          {customerId && (
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-500 dark:text-gray-400">Cliente</span>
                              <span className="font-mono text-[10px] text-gray-700 dark:text-gray-200 truncate">{customerId}</span>
                            </div>
                          )}
                          {campaignId && (
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-gray-500 dark:text-gray-400">Campagna</span>
                              <span className="font-mono text-[10px] text-gray-700 dark:text-gray-200 truncate">{campaignId}</span>
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </div>
                </aside>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-3 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  {attachments.length > 0 && (
                    <span className="flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5" />
                      <span className="font-medium text-gray-700 dark:text-gray-200">
                        {attachments.length} {attachments.length === 1 ? 'allegato' : 'allegati'}
                      </span>
                      <span className="text-gray-400">({formatFileSize(totalAttachmentSize)})</span>
                    </span>
                  )}
                  {isDirty && !sending && (
                    <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      Bozza non salvata
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={requestClose}
                    disabled={sending}
                    data-testid="button-cancel-compose"
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={sending}
                    size="lg"
                    className="min-w-[140px]"
                    data-testid="button-send-email"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Invio...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Invia
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Drag overlay */}
              {isDragging && (
                <div className="absolute inset-0 z-30 bg-blue-500/10 backdrop-blur-[2px] border-4 border-dashed border-blue-500 rounded-xl flex items-center justify-center pointer-events-none">
                  <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3">
                    <Upload className="w-12 h-12 text-blue-600" />
                    <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Rilascia per allegare
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Verranno aggiunti come allegati all'email
                    </p>
                  </div>
                </div>
              )}

              {/* Sending overlay */}
              {sending && (
                <div className="absolute inset-0 z-20 bg-white/40 dark:bg-gray-950/40 backdrop-blur-[1px] flex items-start justify-center pt-20">
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-xl px-5 py-2.5 flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Invio in corso…
                    </span>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm close alert */}
      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent data-testid="alert-confirm-close">
          <AlertDialogHeader>
            <AlertDialogTitle>Vuoi davvero chiudere?</AlertDialogTitle>
            <AlertDialogDescription>
              Le modifiche andranno perse e l'email non verrà inviata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-close">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={performClose}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="button-confirm-close"
            >
              Chiudi senza salvare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
